import { useState, useEffect } from 'react';
import { TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { Tooltip } from '@/shared/components/Tooltip/Tooltip';
import ToggleButton from '@/shared/components/ToggleButton/ToggleButton';
import { Snackbar } from '@/shared/components/Snackbar';
import UpdateStatusDialog from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import plLabels from '../../constants/plLabels.json';

const GENDER_ITEMS = [
  {
    id: 'male',
    label: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_MALE,
    toolTip: false,
  },
  {
    id: 'female',
    label: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_FEMALE,
    toolTip: false,
  },
];
import { EMPLOYEE_ERROR_MESSAGES } from '../../errors';
import { updateEmployee } from '../../api/updateEmployeeApiService';
import { updateEmployeeStatusApiService } from '../../api/updateEmployeeStatusApiService';
import { getEmployeeStatusDialogConfig } from '../EmployeeList/employeeStatusDialogConfig';
import { validateEmployeePhone } from '../../utils/validation';
import type { FacilityUserListItem } from '../../api/listFacilityUserApiService';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EditForm {
  name: string;
  phoneNumber: string;
  gender?: 'male' | 'female';
  academicTitle: string;
}

interface EditFormErrors {
  name: string;
  phoneNumber: string;
}

export interface EditEmployeeFormProps {
  employee: FacilityUserListItem;
  canToggle?: boolean;
  isToggleDisabled?: boolean;
  toggleDisabledReason?: string | undefined;
  onCancel: () => void;
  onSaved: (updated: FacilityUserListItem) => void;
  onToggled?: (updated: FacilityUserListItem) => void;
  onError: (message: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
}

// ── Validation ────────────────────────────────────────────────────────────────

const validateForm = (form: EditForm): EditFormErrors => ({
  name: !form.name.trim() ? EMPLOYEE_ERROR_MESSAGES.REQUIRED : '',
  phoneNumber: validateEmployeePhone(form.phoneNumber),
});

const hasErrors = (errors: EditFormErrors) =>
  Object.values(errors).some(Boolean);

// ── Component ─────────────────────────────────────────────────────────────────

export const EditEmployeeForm = ({
  employee,
  canToggle = false,
  isToggleDisabled = false,
  toggleDisabledReason,
  onCancel,
  onSaved,
  onToggled,
  onError,
  onDirtyChange,
  onSavingChange,
}: EditEmployeeFormProps) => {
  const buildForm = (emp: typeof employee): EditForm => {
    const g = emp.gender as 'male' | 'female' | null | undefined;
    return g
      ? {
          name: emp.name,
          phoneNumber: emp.phone_number ?? '',
          gender: g,
          academicTitle: emp.academic_title ?? '',
        }
      : {
          name: emp.name,
          phoneNumber: emp.phone_number ?? '',
          academicTitle: emp.academic_title ?? '',
        };
  };

  const [form, setForm] = useState<EditForm>(() => buildForm(employee));
  const [errors, setErrors] = useState<EditFormErrors>({
    name: '',
    phoneNumber: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const [currentStatus, setCurrentStatus] = useState<'active' | 'inactive'>(
    employee.status
  );
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [isToggleSubmitting, setIsToggleSubmitting] = useState(false);
  const [isToggleSuccessOpen, setIsToggleSuccessOpen] = useState(false);
  const [toggleSuccessMessage, setToggleSuccessMessage] = useState('');
  const [isToggleErrorOpen, setIsToggleErrorOpen] = useState(false);
  const [toggleErrorMessage, setToggleErrorMessage] = useState('');

  // Re-sync form if the parent swaps in a different employee
  useEffect(() => {
    setForm(buildForm(employee));
    setErrors({ name: '', phoneNumber: '' });
    setCurrentStatus(employee.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employee.id,
    employee.name,
    employee.phone_number,
    employee.gender,
    employee.academic_title,
    employee.status,
  ]);

  const isDirty =
    form.name !== employee.name ||
    form.phoneNumber !== (employee.phone_number ?? '') ||
    form.gender !==
      ((employee.gender as 'male' | 'female' | null | undefined) ??
        undefined) ||
    form.academicTitle !== (employee.academic_title ?? '');

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const updateField = (
    field: keyof Pick<EditForm, 'name' | 'phoneNumber' | 'academicTitle'>,
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field !== 'academicTitle' && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const updateGender = (value: 'male' | 'female' | undefined) => {
    const { gender: _g, ...rest } = form;
    setForm(
      value !== undefined
        ? ({ ...rest, gender: value } satisfies EditForm)
        : (rest as EditForm)
    );
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmClearOpen(true);
    } else {
      onCancel();
    }
  };

  const handleSave = async () => {
    const formErrors = validateForm(form);
    if (hasErrors(formErrors)) {
      setErrors(formErrors);
      return;
    }

    setIsSaving(true);
    onSavingChange?.(true);

    const result = await updateEmployee(employee.id, {
      name: form.name.trim(),
      phoneNumber: form.phoneNumber.trim(),
      ...(form.gender !== undefined && { gender: form.gender }),
      academicTitle: form.academicTitle.trim(),
    });

    setIsSaving(false);
    onSavingChange?.(false);

    if (!result.success) {
      onError(result.error ?? EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR);
      return;
    }

    if (result.employee) {
      onSaved(result.employee);
    }
  };

  const handleToggleConfirm = async () => {
    const isCurrentlyActive = currentStatus === 'active';
    setIsToggleSubmitting(true);
    const result = await updateEmployeeStatusApiService.updateEmployeeStatus({
      employeeId: employee.id,
      isActive: !isCurrentlyActive,
    });
    setIsToggleSubmitting(false);
    setIsToggleDialogOpen(false);

    if (result.success) {
      const newStatus = isCurrentlyActive ? 'inactive' : 'active';
      setCurrentStatus(newStatus);
      setToggleSuccessMessage(
        isCurrentlyActive
          ? FACILITY_LABELS.DEACTIVATE_EMPLOYEE_SUCCESS
          : FACILITY_LABELS.ACTIVATE_EMPLOYEE_SUCCESS
      );
      setIsToggleSuccessOpen(true);
      onToggled?.({ ...employee, status: newStatus });
    } else {
      if (result.isConflict) {
        const actualStatus = isCurrentlyActive ? 'inactive' : 'active';
        setCurrentStatus(actualStatus);
        onToggled?.({ ...employee, status: actualStatus });
      }
      setToggleErrorMessage(
        result.error ?? 'Nie udało się zmienić statusu pracownika.'
      );
      setIsToggleErrorOpen(true);
    }
  };

  return (
    <div className="border border-primary-color-30 p-4 rounded-2xl flex flex-col gap-6">
      <CreateModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirmCancel={() => {
          setConfirmClearOpen(false);
          onCancel();
        }}
      />

      <div className="flex items-center justify-between">
        <p className="text-[18px] font-normal text-neutral-10">
          {FACILITY_LABELS.EDIT_EMPLOYEE_MODAL_TITLE}
        </p>

        {/* Status toggle — top-right */}
        {(() => {
          const isActive = currentStatus === 'active';
          const effectivelyDisabled = isToggleDisabled || !canToggle;
          const tooltipContent =
            isToggleDisabled && toggleDisabledReason
              ? toggleDisabledReason
              : !canToggle
                ? plLabels.common.noPermission
                : null;

          const toggleBtn = (
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton checked={isActive} onChange={() => {}} />
            </div>
          );

          return (
            <div
              className={`flex items-center gap-2 shrink-0 ${
                effectivelyDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              onClick={
                !effectivelyDisabled
                  ? () => setIsToggleDialogOpen(true)
                  : undefined
              }
            >
              {tooltipContent ? (
                <Tooltip
                  content={tooltipContent}
                  forceShow
                  triggerWrapContent
                  contentMinWidth={200}
                >
                  {toggleBtn}
                </Tooltip>
              ) : (
                toggleBtn
              )}
              <span
                className="text-sm w-[76px] shrink-0"
                style={{ color: '#181A1B', fontWeight: 400 }}
              >
                {isActive
                  ? FACILITY_LABELS.STATUS_ACTIVE
                  : FACILITY_LABELS.STATUS_INACTIVE}
              </span>
            </div>
          );
        })()}
      </div>

      {/* Row 1: Name | Employee ID | Email */}
      <div className="flex gap-4">
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_NAME}
            dataTestId="edit-employee-name"
            autoTrimMaxLength={200}
            value={form.name}
            onChange={(e) => updateField('name', e.target.value.trimStart())}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.EDIT_EMPLOYEE_FIELD_USER_ID}
            dataTestId="edit-employee-user-id"
            value={employee.user_id ?? '—'}
            disabled
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_EMAIL}
            dataTestId="edit-employee-email"
            value={employee.email}
            disabled
          />
        </div>
      </div>

      {/* Row 2: Phone | Gender | Academic Title */}
      <div className="flex gap-4">
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_PHONE}
            dataTestId="edit-employee-phone"
            type="tel"
            value={form.phoneNumber}
            onChange={(e) =>
              updateField(
                'phoneNumber',
                e.target.value.replace(/\D/g, '').slice(0, 9)
              )
            }
            error={Boolean(errors.phoneNumber)}
            errorMessage={errors.phoneNumber}
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <Dropdown
            items={GENDER_ITEMS}
            {...(form.gender !== undefined && { selectedId: form.gender })}
            onItemClick={(id) => updateGender(id as 'male' | 'female')}
            menuFullWidth
            disabled={isSaving}
            trigger={({ open, toggle, disabled }) => {
              const selectedLabel = form.gender
                ? GENDER_ITEMS.find((g) => g.id === form.gender)?.label
                : undefined;
              return (
                <DropDownButton
                  label={
                    <>
                      {FACILITY_LABELS.FIELD_EMPLOYEE_GENDER}
                      <span className="text-bodySmall font-normal">
                        {' '}
                        {FACILITY_LABELS.FIELD_OPTIONAL}
                      </span>
                    </>
                  }
                  open={open}
                  toggle={toggle}
                  disabled={disabled}
                  data-testid="employee-gender"
                  {...(selectedLabel !== undefined && { value: selectedLabel })}
                />
              );
            }}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={
              <>
                {FACILITY_LABELS.FIELD_EMPLOYEE_ACADEMIC_TITLE}
                <span className="text-bodySmall font-normal">
                  {' '}
                  {FACILITY_LABELS.FIELD_OPTIONAL}
                </span>
              </>
            }
            dataTestId="edit-employee-academic-title"
            autoTrimMaxLength={200}
            value={form.academicTitle}
            onChange={(e) =>
              updateField('academicTitle', e.target.value.trimStart())
            }
            disabled={isSaving}
          />
        </div>
      </div>

      <div className="mt-[16px] -mx-4 border-b-2 border-[#DCDDE0]" />

      <div className="flex justify-end gap-4 mt-[-8px]">
        <Button
          label={FACILITY_LABELS.CANCEL_BUTTON}
          buttonType="Square"
          type="button"
          onClick={handleCancel}
          disabled={isSaving}
          className="!bg-transparent disabled:!bg-neutral-10/15 !px-3 !py-1.5 cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant disabled:!text-neutral-10/[0.38] hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
        />
        <Button
          label={
            isSaving
              ? FACILITY_LABELS.EDIT_EMPLOYEE_SAVE_BUTTON_LOADING
              : FACILITY_LABELS.EDIT_EMPLOYEE_SAVE_BUTTON
          }
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !isDirty}
          className="!px-3 !py-1.5 text-labelLarge cursor-pointer !rounded-[8px]"
        />
      </div>
      {isToggleDialogOpen &&
        (() => {
          const config = getEmployeeStatusDialogConfig(
            employee.name,
            currentStatus === 'active'
          );
          return (
            <UpdateStatusDialog
              open={true}
              onClose={() => setIsToggleDialogOpen(false)}
              onConfirm={() => void handleToggleConfirm()}
              isSubmitting={isToggleSubmitting}
              {...config}
            />
          );
        })()}

      <Snackbar
        open={isToggleSuccessOpen}
        message={toggleSuccessMessage}
        variant="success"
        onClose={() => setIsToggleSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isToggleErrorOpen}
        message={toggleErrorMessage}
        variant="error"
        onClose={() => setIsToggleErrorOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </div>
  );
};
